import { memo } from 'react';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import type {
    HistoryBucket,
    HistorySeriesPoint
} from '../../../services/dmMonitoringOverviewHistory';
import { AXIS_TICK, CHART_RESIZE_DEBOUNCE_MS, HOVER_CURSOR } from '../../../utils/chartFormatting';
import { activityRows, hasActivity, type ActivityRow, type ActivityScope } from './overviewSeries';
import type { PerPersonMeasure } from './perPersonRows';
import { EmptyNote } from './StackedBar';

export const ACTIVITY_COLORS = {
    raised: '#99f6e4', // teal-200
    resolved: '#007E87' // brand teal
} as const;

export const ACTIVITY_SERIES_LABELS = {
    events: { raised: 'Raised', resolved: 'Resolved' },
    patients: { raised: 'Raised (patients)', resolved: 'Resolved (patients)' }
} as const;

export const ACTIVITY_CHART_HEIGHT_PX = 240;
const CHART_MARGIN = { top: 8, right: 4, left: -16, bottom: 0 } as const;
const MAX_BAR_PX = 40;
const MIN_TICK_GAP_PX = 16;

interface ActivityTooltipProps {
    measure: PerPersonMeasure;
    active?: boolean;
    payload?: { payload: ActivityRow }[];
}

function ActivityTooltip({ measure, active, payload }: ActivityTooltipProps) {
    const row = active ? payload?.[0]?.payload : undefined;
    if (!row) return null;
    const entries = [
        { key: 'raised', value: row.raised },
        { key: 'resolved', value: row.resolved }
    ] as const;
    return (
        <div className="min-w-[150px] rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 shadow-lg">
            <p className="mb-1.5 text-xs font-bold text-gray-800">{row.label}</p>
            <div className="flex flex-col gap-1">
                {entries.map((entry) => (
                    <div key={entry.key} className="flex items-center gap-2">
                        <span
                            className="h-2 w-2 shrink-0 rounded-[2px]"
                            style={{ background: ACTIVITY_COLORS[entry.key] }}
                        />
                        <span className="flex-1 text-[11px] text-gray-500">
                            {ACTIVITY_SERIES_LABELS[measure][entry.key]}
                        </span>
                        <span className="text-[11px] font-semibold tabular-nums text-gray-900">
                            {entry.value.toLocaleString()}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

interface ActivityChartProps {
    series: readonly HistorySeriesPoint[];
    bucket: HistoryBucket;
    scope: ActivityScope;
    measure: PerPersonMeasure;
}

// Memoised on the history response, which live polls never touch, so polls leave the chart alone.
export const ActivityChart = memo(function ActivityChart({
    series,
    bucket,
    scope,
    measure
}: ActivityChartProps) {
    const rows = activityRows(series, bucket, scope, measure);
    if (rows === null) {
        return (
            <div className="grid place-items-center" style={{ height: ACTIVITY_CHART_HEIGHT_PX }}>
                <EmptyNote>Patient counts aren't available yet.</EmptyNote>
            </div>
        );
    }
    if (!hasActivity(rows)) {
        return (
            <div className="grid place-items-center" style={{ height: ACTIVITY_CHART_HEIGHT_PX }}>
                <EmptyNote>Nothing raised or resolved in this period.</EmptyNote>
            </div>
        );
    }
    return (
        <ResponsiveContainer
            width="100%"
            height={ACTIVITY_CHART_HEIGHT_PX}
            debounce={CHART_RESIZE_DEBOUNCE_MS}
            className="overflow-x-clip"
        >
            <ComposedChart data={rows} margin={CHART_MARGIN} barCategoryGap="24%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                    dataKey="label"
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={6}
                    interval="preserveStartEnd"
                    minTickGap={MIN_TICK_GAP_PX}
                />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                    cursor={HOVER_CURSOR}
                    content={<ActivityTooltip measure={measure} />}
                    isAnimationActive={false}
                />
                <Bar
                    dataKey="raised"
                    name={ACTIVITY_SERIES_LABELS[measure].raised}
                    fill={ACTIVITY_COLORS.raised}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={MAX_BAR_PX}
                    isAnimationActive={false}
                />
                <Line
                    type="monotone"
                    dataKey="resolved"
                    name={ACTIVITY_SERIES_LABELS[measure].resolved}
                    stroke={ACTIVITY_COLORS.resolved}
                    strokeWidth={2}
                    dot={{ r: 2.5, strokeWidth: 0, fill: ACTIVITY_COLORS.resolved }}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                />
            </ComposedChart>
        </ResponsiveContainer>
    );
});
